import { useMemo } from 'react';

import { Box, Divider, Paper, Skeleton, Typography } from '@/shared/ui';

import { useUserDateFormat } from '@/features/_shared/user';
import { useFactsheetMapImage } from '@/features/factsheet/api/queries';
import {
  formatFactsheetDate,
  formatFactsheetDateWithFallback
} from '@/features/factsheet/lib/dateFormatting';
import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';
import type { FactsheetDetails } from '@/features/factsheet/types';

/**
 * Inserts zero-width spaces after common break characters to allow
 * long text to wrap gracefully without breaking layout.
 */
const insertSoftBreaks = (text: string): string => text.replaceAll(/([ _-])/g, '$1\u200B');

interface MapImageProps {
  location: string;
  zoom: string;
}

const MapImage = ({ location, zoom }: MapImageProps) => {
  const { data: imageSrc, isError } = useFactsheetMapImage(location, zoom);
  const isCompact = useIsFactsheetCompact();

  if (isError) {
    return null;
  }

  return (
    <Box sx={{ width: isCompact ? '100%' : '40%' }}>
      {imageSrc ? (
        <img
          alt='factsheetImage'
          src={imageSrc}
          style={{ aspectRatio: 1, width: '100%' }}
        />
      ) : (
        <Skeleton
          sx={{ aspectRatio: 1, width: '100%', height: 'auto' }}
          variant='rectangular'
        />
      )}
    </Box>
  );
};

interface TileProps {
  label: string;
  value: string | boolean | string[] | undefined;
}

const Tile = ({ label, value }: TileProps) => {
  const isCompact = useIsFactsheetCompact();

  if (!value) return null;

  const renderValue = () => {
    if (Array.isArray(value)) {
      return (
        <>
          {value.map((item, index) => (
            <Typography
              key={`${item}-${index}`}
              size={isCompact ? 14 : undefined}
              sx={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word'
              }}
            >
              {insertSoftBreaks(item)}
            </Typography>
          ))}
        </>
      );
    }

    return (
      <Typography
        size={isCompact ? 14 : undefined}
        sx={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word'
        }}
      >
        {insertSoftBreaks(value.toString())}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        p: isCompact ? 0 : 1.5,
        overflow: 'hidden',
        minWidth: isCompact ? 0 : 180,
        maxWidth: 180,
        wordBreak: 'break-word'
      }}
    >
      <Typography
        size={isCompact ? 14 : undefined}
        sx={{ fontWeight: 700 }}
      >
        {label}
      </Typography>
      {renderValue()}
    </Box>
  );
};

interface DetailsSectionProps {
  children: React.ReactNode;
}

const DetailsSection = ({ children }: DetailsSectionProps) => {
  const isCompact = useIsFactsheetCompact();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isCompact ? 'repeat(auto-fill, minmax(120px, 1fr))' : 'repeat(5, 1fr)',
        alignItems: 'flex-start',
        gap: 2
      }}
    >
      {children}
    </Box>
  );
};

interface DetailsTabProps {
  factsheet: FactsheetDetails;
  isAuthenticated: boolean;
}

const DetailsTab = ({ factsheet, isAuthenticated }: DetailsTabProps) => {
  const dateFormat = useUserDateFormat();
  const { indexDetails, symbols, mapLocation, mapZoom } = factsheet;
  const isCompact = useIsFactsheetCompact();

  const startDate = useMemo(
    () => formatFactsheetDate(indexDetails['startDate'] as string, dateFormat.value),
    [dateFormat.value, indexDetails]
  );

  const endDate = useMemo(
    () =>
      formatFactsheetDateWithFallback(
        indexDetails['endDate'] as string | undefined,
        dateFormat.value,
        '(ongoing)'
      ),
    [dateFormat.value, indexDetails]
  );

  const primaryTiles: TileProps[] = [
    { label: 'Currency / Units', value: `${indexDetails['currency']} / ${indexDetails['units']}` },
    { label: 'Frequency', value: indexDetails['frequency'] },
    { label: 'Pricing Basis', value: indexDetails['pricingBasis'] },
    { label: 'Timezone', value: indexDetails['timeZone'] },
    { label: 'Trading Hub', value: indexDetails['tradingHub'] },
    { label: 'Delivery Basis', value: indexDetails['deliveryBasis'] },
    { label: 'Commodity', value: indexDetails['commodity'] },
    { label: 'Index Module(s)', value: indexDetails['indexModules'] },
    { label: 'Holiday Calendar', value: indexDetails['holidayCalendar'] },
    { label: 'Last Trade Calendar', value: indexDetails['lastTradeCalendar'] },
    { label: 'Columns Available', value: indexDetails['columnsAvailable'] },
    { label: 'Increment', value: indexDetails['increment'] }
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isCompact ? 'column' : 'row',
        gap: isCompact ? 1 : '24px'
      }}
    >
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: isCompact ? 2 : 0 }}>
        {/* Primary details section */}
        <DetailsSection>
          {primaryTiles.map(tile => (
            <Tile
              key={tile.label}
              {...tile}
            />
          ))}
        </DetailsSection>

        <Divider sx={{ backgroundColor: 'gx.tileLight', pr: 4 }} />

        {/* Status and dates section */}
        <DetailsSection>
          <Tile
            label='Status'
            value={indexDetails['status']}
          />
          <Tile
            label='Start Date'
            value={startDate}
          />
          <Tile
            label='End Date'
            value={endDate}
          />
        </DetailsSection>

        <Divider sx={{ backgroundColor: 'gx.tileLight', pr: 4 }} />

        {/* Symbol information section */}
        <DetailsSection>
          <Tile
            label='Spot Alias'
            value={indexDetails['spotAlias'] || '(none)'}
          />
          <Tile
            label='Forward Curve'
            value={indexDetails['forwardCurve'] ? 'Yes' : 'No'}
          />
          <Tile
            label='Num Items'
            value={symbols?.length.toString()}
          />
        </DetailsSection>
      </Paper>

      {isAuthenticated && mapLocation && (
        <MapImage
          location={mapLocation}
          zoom={mapZoom ?? '10'}
        />
      )}
    </Box>
  );
};

export default DetailsTab;
